import clsx from "clsx";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";

import {
  appointmentDurationUnits,
  APPT_DEFAULT_DURATION,
  APPT_DEFAULT_DURATION_UNIT,
  bufferTimes,
  endWorkingTimeOfDay,
  minimumNoticesInMinute,
  startWorkingTimeOfDay,
} from "@smart/bridge-intake-components-dom";
import {
  MeetingType,
  TimeOfDay,
  meetingTypeLabel,
  meetingTypeValue,
  DurationUnit,
} from "@smart/bridge-types-basic";
import {
  Alert,
  FieldList,
  Icon,
  Label,
  NumberInput,
  RadioButtons,
  Select,
  Switch,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import {
  TimeZone,
  buildTimeZoneList,
  getLocalTimeZoneCode,
  removeKeysRecursively,
  findLocalTimezone,
  getLongestTimeSpanInMinutes,
} from "@smart/itops-utils-basic";
import {
  communicationMethodIcon,
  communicationMethodLabel,
} from "@smart/manage-hooks-dom";

import { AvailabilityItem } from "./availability-item";
import { baseLanguageKey, LanguageKey } from "../../../../types";
import { GqlFieldValues, OnUpdateField } from "../../types";
import { formatDuration } from "../../utils";

type AppointmentProps = {
  onUpdateField: OnUpdateField;
  field: GqlFieldValues;
  viewingLanguage: LanguageKey;
};

export const Appointment = ({
  field: fieldValues,
  onUpdateField,
  viewingLanguage,
}: AppointmentProps) => {
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;
  const {
    availableStaffIds,
    availability,
    duration,
    durationUnit,
    bufferTime,
    minimumNotice,
    timezone,
    meetingType,
    skipEventInvite,
  } = fieldValues;

  const { staff } = useSmokeballApp();

  const allActiveStaff =
    staff?.current?.filter((s) => s.enabled && !s.former) || [];

  const buildFullName = ({
    firstName,
    middleName,
    lastName,
  }: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
  }) => [firstName, middleName, lastName].filter(Boolean).join(" ");
  const timeZoneOptions = buildTimeZoneList().map((tz) => ({
    ...tz,
    label: tz.label.split("/").pop() || "",
  }));
  const timeZoneOptionsByRegion = timeZoneOptions.reduce(
    (
      groupByRegions: { region: string; items: typeof timeZoneOptions }[],
      option,
    ) => {
      const region = groupByRegions.find((r) => r.region === option.region);
      if (!region) {
        groupByRegions.push({
          region: option.region,
          items: [option],
        });
      } else {
        region.items.push(option);
      }
      return groupByRegions;
    },
    [],
  );
  const localTimeZone = findLocalTimezone(timeZoneOptions);

  const timezoneValue = timeZoneOptions.find((tz) => tz.tzCode === timezone);

  const formMethods = useForm<{
    availableStaff: { id: string; fullName: string }[];
    duration: number;
    durationUnit: DurationUnit;
    availability?:
      | {
          day: number;
          fromTime: TimeOfDay;
          toTime: TimeOfDay;
          enabled: boolean;
        }[]
      | null;
    bufferTime?: number | null;
    minimumNotice?: number | null;
    meetingType: MeetingType;
    timezone?: TimeZone;
    skipEventInvite?: boolean;
  }>({
    defaultValues: {
      availableStaff: allActiveStaff
        .filter((s) => availableStaffIds?.includes(s.id))
        .map((s) => ({
          id: s.id,
          fullName: buildFullName(s),
        })),
      duration: duration || APPT_DEFAULT_DURATION,
      durationUnit: durationUnit || APPT_DEFAULT_DURATION_UNIT,
      availability: availability?.map((a) =>
        removeKeysRecursively(a, ["__typename"]),
      ),
      bufferTime,
      minimumNotice,
      meetingType: meetingType || "inPerson",
      timezone: timezoneValue || localTimeZone,
      skipEventInvite: !!skipEventInvite,
    },
  });

  const { control, handleSubmit, watch, setValue } = formMethods;

  const submit = handleSubmit(async (values) => {
    await onUpdateField({
      field: fieldValues,
      updated: {
        availableStaffIds: values.availableStaff.map((s) => s.id),
        duration: values.duration || APPT_DEFAULT_DURATION,
        durationUnit: values.durationUnit || APPT_DEFAULT_DURATION_UNIT,
        availability: values.availability || undefined,
        bufferTime: values.bufferTime || undefined,
        minimumNotice: values.minimumNotice || undefined,
        meetingType: values.meetingType,
        timezone: values.timezone?.tzCode || getLocalTimeZoneCode(),
        skipEventInvite: values.skipEventInvite,
      },
    });
  });

  const availabilityOptions = useFieldArray({ control, name: "availability" });
  const selectedMeetingType = watch("meetingType");
  const selectedDurationUnit = watch("durationUnit");
  const selectedDuration = watch("duration");
  const selectedSkipEventInvite = watch("skipEventInvite");

  const displayAlert = () => {
    if (!availability) return false;
    const longestTimeSpanInMinutes = getLongestTimeSpanInMinutes(availability);
    const durationInMinutes =
      selectedDurationUnit === "hours"
        ? selectedDuration * 60
        : selectedDuration;

    return durationInMinutes > longestTimeSpanInMinutes;
  };

  return (
    <FormProvider {...formMethods}>
      <FieldList className="text-ui mt-2 px-[0.4rem]">
        <Controller
          control={control}
          name="availableStaff"
          render={({ field, fieldState }) => (
            <div data-testid="appointment-available-staff">
              <Select
                id={field.name}
                label="Select team members"
                title="Available team members"
                placeholder="Select team members"
                options={allActiveStaff.map((s) => ({
                  value: s,
                  label: buildFullName(s),
                }))}
                empty="No team members available"
                error={!!fieldState.error}
                message={fieldState.error?.message}
                value={field.value}
                valueComparer={(o, v) => o?.id === v?.id}
                onChange={async (v) => {
                  field.onChange(v);
                  await submit();
                }}
                disabled={!isViewingBaseLanguage}
                isMultiple
              />
            </div>
          )}
        />
        <div className="flex items-start justify-between gap-[2.4rem]">
          <div className="flex gap-small">
            <Controller
              control={control}
              name="duration"
              render={({ field, fieldState }) => (
                <div className="w-[6.5rem]">
                  <NumberInput
                    id={field.name}
                    label="Duration"
                    error={!!fieldState.error}
                    message={fieldState.error?.message}
                    value={field.value}
                    onChange={async (v) => {
                      field.onChange(v);
                    }}
                    onBlur={async () => {
                      if (!selectedDuration) {
                        setValue("duration", APPT_DEFAULT_DURATION);
                        setValue("durationUnit", APPT_DEFAULT_DURATION_UNIT);
                      }
                      await submit();
                    }}
                    dataTestId="appointment-duration"
                    disabled={!isViewingBaseLanguage}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="durationUnit"
              render={({ field, fieldState }) => (
                <div
                  data-testid="appointment-duration-unit"
                  className="mt-10 w-48"
                >
                  <Select
                    id={field.name}
                    options={appointmentDurationUnits.map((u) => ({
                      label: u,
                      value: u,
                    }))}
                    empty="No options"
                    error={!!fieldState.error}
                    message={fieldState.error?.message}
                    value={field.value}
                    onChange={async (v) => {
                      field.onChange(v);
                      await submit();
                    }}
                    disabled={!isViewingBaseLanguage}
                  />
                </div>
              )}
            />
          </div>
          <Controller
            control={control}
            name="bufferTime"
            render={({ field, fieldState }) => (
              <div data-testid="appointment-buffer-time" className="w-full">
                <Select
                  className="flex-1"
                  id={field.name}
                  label="Buffer"
                  title="Buffer time"
                  options={bufferTimes.map((b) => ({
                    value: b,
                    label: `${b} minutes`,
                  }))}
                  empty="No options"
                  error={!!fieldState.error}
                  message={fieldState.error?.message}
                  value={field.value}
                  onChange={async (v) => {
                    field.onChange(v);
                    await submit();
                  }}
                  disabled={!isViewingBaseLanguage}
                />
              </div>
            )}
          />
          <Controller
            control={control}
            name="minimumNotice"
            render={({ field, fieldState }) => (
              <div data-testid="appointment-minimum-notice" className="w-full">
                <Select
                  className="flex-1"
                  id={field.name}
                  label="Minimum notice"
                  options={minimumNoticesInMinute.map((m) => ({
                    value: m,
                    label: formatDuration(m),
                  }))}
                  empty="No options"
                  error={!!fieldState.error}
                  message={fieldState.error?.message}
                  value={field.value}
                  onChange={async (v) => {
                    field.onChange(v);
                    await submit();
                  }}
                  disabled={!isViewingBaseLanguage}
                />
              </div>
            )}
          />
        </div>
        {displayAlert() && (
          <Alert
            content="Selected appointment duration is longer than your available hours. Please shorten the duration or modify your available hours."
            variant="section"
            palette="warning"
          />
        )}
        <div id="availability" data-testid="appointment-availability">
          <div
            className={clsx(
              "text-ui font-semibold mb-2",
              !isViewingBaseLanguage && "text-neutral-400",
            )}
          >
            Available hours
          </div>
          {availabilityOptions.fields.map((o, index) => (
            <AvailabilityItem
              key={o.id}
              dailyAvailability={o}
              onChange={async (item) => {
                availabilityOptions.update(index, {
                  ...item,
                  fromTime: item.fromTime || startWorkingTimeOfDay,
                  toTime: item.toTime || endWorkingTimeOfDay,
                });
                await submit();
              }}
              disabled={!isViewingBaseLanguage}
            />
          ))}
        </div>
        <div>
          <div className="flex gap-4">
            <Controller
              control={control}
              name="meetingType"
              render={({ field, fieldState }) => (
                <div
                  className="flex-1 pl-1"
                  data-testid="appointment-meeting-type"
                >
                  <RadioButtons
                    id={field.name}
                    label="Type"
                    error={!!fieldState.error}
                    message={fieldState.error?.message}
                    options={meetingTypeValue.map((t) => ({
                      value: t,
                      label: meetingTypeLabel[t],
                    }))}
                    onChange={async (v) => {
                      field.onChange(v);
                      await submit();
                    }}
                    value={field.value}
                    disabled={!isViewingBaseLanguage}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="timezone"
              render={({ field, fieldState }) => (
                <div
                  data-testid="appointment-time-zone"
                  className="flex-1 ml-6"
                >
                  <Select
                    id={field.name}
                    label="Timezone"
                    options={timeZoneOptionsByRegion.map((byRegion) => ({
                      heading: byRegion.region,
                      items: byRegion.items.map((tz) => ({
                        value: tz,
                        label: tz.label,
                      })),
                    }))}
                    empty="No options"
                    error={!!fieldState.error}
                    message={fieldState.error?.message}
                    valueComparer={(o, v) => o?.tzCode === v?.tzCode}
                    value={field.value}
                    onChange={async (v) => {
                      field.onChange(v);
                      await submit();
                    }}
                    disabled={!isViewingBaseLanguage}
                  />
                </div>
              )}
            />
          </div>
          {selectedMeetingType === "telephoneConsult" && (
            <div className="flex items-center gap-1 mt-2 ml-3 text-neutral-600">
              <Icon className="w-4 h-4 mr-2" name="regularCircleExclamation" />
              <div className="text-caption font-normal">
                A mandatory phone number field should be added to the form
              </div>
            </div>
          )}
        </div>
        <div className="mb-4">
          <Controller
            control={control}
            name="skipEventInvite"
            render={({ field, fieldState }) => (
              <Label text="Notification" disabled={!isViewingBaseLanguage}>
                <Switch
                  text={
                    <div className="flex items-center justify-center gap-2">
                      <Icon
                        name={communicationMethodIcon.communicate!.name}
                        mask={communicationMethodIcon.communicate!.mask}
                        className={
                          communicationMethodIcon.communicate!.className
                        }
                      />
                      <span>
                        Send via the {communicationMethodLabel.communicate}
                      </span>
                    </div>
                  }
                  toggled={!selectedSkipEventInvite}
                  onClick={async () => {
                    field.onChange(!selectedSkipEventInvite);
                    await submit();
                  }}
                  error={!!fieldState.error}
                  disabled={!isViewingBaseLanguage}
                  dataTestId="appointment-skip-event-invite-switch"
                />
              </Label>
            )}
          />
        </div>
      </FieldList>
    </FormProvider>
  );
};
